import { Box } from '@mui/material';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { normalizeObjectKeys } from 'utils/normalizeObjectKeys';

import { NamespacesList } from 'core/enums/common';

import {
	TestCaseBox,
	TestDescription,
	TestPlanSubTitle,
	TestPlanTitle,
	UserStoryTest,
	UserStoryTests,
} from 'components/TicketSteps/TestCasesStep/UserStoryTests/styled';
import { ITestCase, ITestPlan, IUserStoryTest } from 'components/TicketSteps/TestCasesStep/types';

type RenderFunction = (
	test: IUserStoryTest,
	oldValues: Array<IUserStoryTest>,
	newValues: Array<IUserStoryTest>,
	testIndex: number,
	isNewBlock: boolean
) => JSX.Element;

export const UserTestCasesChangesDisplay = ({
	oldValue,
	newValue,
}: {
	oldValue: Array<IUserStoryTest>;
	newValue: Array<IUserStoryTest>;
}) => {
	const { t: testCasesTranslate } = useTranslation(NamespacesList['testCasesStep']);

	const updatedUserStoryTests = (userStoryTests: IUserStoryTest[]) =>
		userStoryTests.map((tests: any) => ({
			...tests,
			testScenarios: tests.testScenarios.trim().split('. '),
			testCases: tests.testCases.map((item: any) => ({
				...item,
				isDeleted: false,
				testSteps: item.testSteps.trim().split('. '),
			})),
		}));

	const updatedOldValue = useMemo(() => updatedUserStoryTests(oldValue), [oldValue]);
	const updatedNewValue = useMemo(() => updatedUserStoryTests(newValue), [newValue]);

	const getUpdatedValueStyles = ({
		oldValue,
		newValue,
		showStyles,
	}: {
		oldValue: string;
		newValue: string;
		showStyles: boolean;
	}) => {
		if (showStyles) {
			if (oldValue.trim().replace(/\.$/, '') !== newValue.trim().replace(/\.$/, '')) {
				return { backgroundColor: '#01C8604C' };
			}
		}
		if (!newValue && oldValue) {
			return { backgroundColor: '#FB0B244C' };
		}

		return {};
	};

	const getUserStoriesBlock = ({ isNewBlock }: { isNewBlock: boolean }) => {
		const usedValue: Array<IUserStoryTest> = isNewBlock ? updatedNewValue : updatedOldValue;

		const renderTestDescription = (
			oldValue: string,
			newValue: string,
			showStyles: boolean,
			content: string,
			translateKey?: string
		) => (
			<TestDescription
				sx={{
					...getUpdatedValueStyles({ oldValue, newValue, showStyles }),
				}}
			>
				{translateKey && <span>{testCasesTranslate(translateKey)}:</span>} {content}
			</TestDescription>
		);

		const renderTestCase = (
			testCase: ITestCase,
			oldValues: ITestCase,
			newValues: ITestCase,
			isNewBlock: boolean
		) => (
			<li key={testCase.testCaseId} style={{ marginBottom: '13px' }}>
				<TestCaseBox>
					{renderTestDescription(
						oldValues.description,
						newValues.description,
						isNewBlock,
						`${testCase.testCaseId}: ${testCase.description}`
					)}
					{renderTestDescription(
						oldValues.preConditions,
						newValues.preConditions,
						isNewBlock,
						testCase.preConditions,
						'testCases.preConditions'
					)}
					<TestDescription>
						<span>{testCasesTranslate('testCases.testSteps')}:</span>
					</TestDescription>
					<ol>
						{testCase.testSteps.map((step, stepIndex) => (
							<li key={step}>
								{renderTestDescription(
									oldValues.testSteps[stepIndex],
									newValues.testSteps[stepIndex],
									isNewBlock,
									step
								)}
							</li>
						))}
					</ol>
					{renderTestDescription(
						oldValues.testData,
						newValues.testData,
						isNewBlock,
						testCase.testData,
						'testCases.testData'
					)}
					{renderTestDescription(
						oldValues.expectedResult,
						newValues.expectedResult,
						isNewBlock,
						testCase.expectedResult,
						'testCases.expectedResult'
					)}
				</TestCaseBox>
			</li>
		);

		const renderTestCases: RenderFunction = (test, oldValues, newValues, testIndex, isNewBlock) => (
			<Box>
				<TestPlanSubTitle>{testCasesTranslate(`testCases.title`)}:</TestPlanSubTitle>
				<ol style={{ margin: '0' }}>
					{test.testCases.map((testCase, testCaseId) =>
						renderTestCase(
							testCase,
							oldValues[testIndex].testCases[testCaseId],
							newValues[testIndex].testCases[testCaseId],
							isNewBlock
						)
					)}
				</ol>
			</Box>
		);

		const renderTestScenarios: RenderFunction = (
			test,
			oldValues,
			newValues,
			testIndex,
			isNewBlock
		) => (
			<Box>
				<TestPlanSubTitle>{testCasesTranslate(`testScenarios.title`)}:</TestPlanSubTitle>
				<ol style={{ margin: '0' }}>
					{test.testScenarios.map((scenario, scenarioIndex) => (
						<li key={scenario}>
							{renderTestDescription(
								oldValues[testIndex].testScenarios[scenarioIndex],
								newValues[testIndex].testScenarios[scenarioIndex],
								isNewBlock,
								scenario
							)}
						</li>
					))}
				</ol>
			</Box>
		);

		const renderTestPlan: RenderFunction = (test, oldValues, newValues, testIndex, isNewBlock) => (
			<>
				{(Object.entries(test.testPlan) as Array<[keyof ITestPlan, string]>).map(([key, value]) =>
					key === 'id' ? null : (
						<Box key={key}>
							<TestPlanSubTitle>{testCasesTranslate(`testPlan.${key}`)}:</TestPlanSubTitle>
							{renderTestDescription(
								oldValues[testIndex].testPlan[key],
								newValues[testIndex].testPlan[key],
								isNewBlock,
								value
							)}
						</Box>
					)
				)}
				{renderTestScenarios(test, oldValues, newValues, testIndex, isNewBlock)}
				{renderTestCases(test, oldValues, newValues, testIndex, isNewBlock)}
			</>
		);

		const renderUserStoryTest: RenderFunction = (
			test,
			oldValues,
			newValues,
			testIndex,
			isNewBlock
		) => (
			<UserStoryTest key={test.id}>
				<TestPlanTitle>
					<span>{testCasesTranslate('testPlan.title')}:</span>{' '}
					{normalizeObjectKeys(test)['usecasetitle']}
				</TestPlanTitle>
				{test && renderTestPlan(test, oldValues, newValues, testIndex, isNewBlock)}
			</UserStoryTest>
		);

		const renderUserStoryTests = (
			usedValue: Array<IUserStoryTest>,
			oldValues: Array<IUserStoryTest>,
			newValues: Array<IUserStoryTest>,
			isNewBlock: boolean
		) => (
			<UserStoryTests>
				{usedValue?.map((test, testIndex) =>
					renderUserStoryTest(test, oldValues, newValues, testIndex, isNewBlock)
				)}
			</UserStoryTests>
		);

		return renderUserStoryTests(usedValue, updatedOldValue, updatedNewValue, isNewBlock);
	};

	return (
		<Box sx={{ display: 'flex', maxHeight: '90%', gap: '24px', overflow: 'auto' }}>
			{getUserStoriesBlock({ isNewBlock: false })}
			{getUserStoriesBlock({ isNewBlock: true })}
		</Box>
	);
};
